/**
 * Утилиты для санитизации HTML и защиты от XSS
 */
import DOMPurify from 'dompurify';

/**
 * Конфигурация DOMPurify для Creator Platform
 * Разрешает безопасные теги для форматирования markdown
 */
const SAFE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'del', 'code', 'span',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    'blockquote', 'pre',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
    'a', 'hr', 'div',
  ] as string[],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class', 'id'] as string[],
  // Запрещаем опасные протоколы
  FORBID_ATTR: ['style', 'onerror', 'onclick', 'onload', 'onmouseover'] as string[],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'] as string[],
  RETURN_TRUSTED_TYPE: false as const,
};

/**
 * Основная функция санитизации с поддержкой безопасных markdown тегов
 * @param dirty - HTML строка, которая может содержать XSS
 * @returns Безопасный HTML
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(dirty, SAFE_CONFIG) as string;
}

/**
 * Раскодирует HTML-сущности, которые некоторые AI-провайдеры (в первую очередь
 * perplexity/sonar-reasoning-pro — verification-модель для анализа рынка) возвращают в
 * обычном тексте вместо сырых символов: `&gt;`, `&quot;`, `&amp;`, `&#39;`, …
 *
 * Без этого текста с сущностями сохраняется как есть, а потом повторно
 * экранируется в MarkdownRenderer (`&gt;` → `&amp;gt;`) — и `&gt;` / `&quot;`
 * видны в документе буквально.
 *
 * `&amp;` раскрывается ПОСЛЕДНИМ, чтобы декодирование было одноуровневым:
 * `&amp;gt;` (осознанно написанный «&gt;») превращается в «&gt;», а не в «>».
 */
const NAMED_ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&#039;': "'",
  '&nbsp;': ' ',
  '&#160;': ' ',
  '&ndash;': '–',
  '&mdash;': '—',
  '&hellip;': '…',
  '&laquo;': '«',
  '&raquo;': '»',
  '&rsquo;': '’',
  '&lsquo;': '‘',
  '&ldquo;': '“',
  '&rdquo;': '”',
};

export function decodeHtmlEntities(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  let out = text.replace(
    /&(?:lt|gt|quot|apos|#0?39|#160|nbsp|ndash|mdash|hellip|laquo|raquo|rsquo|lsquo|ldquo|rdquo);/g,
    (m) => NAMED_ENTITIES[m] ?? m,
  );

  out = out.replace(/&#(\d+);/g, (m, dec: string) => {
    const code = Number.parseInt(dec, 10);
    return Number.isFinite(code) ? String.fromCodePoint(code) : m;
  });
  out = out.replace(/&#x([0-9a-fA-F]+);/g, (m, hex: string) => {
    const code = Number.parseInt(hex, 16);
    return Number.isFinite(code) ? String.fromCodePoint(code) : m;
  });

  return out.replace(/&amp;/g, '&');
}

/**
 * Escape HTML специальных символов (для использования в text nodes)
 * @param text - текст для экранирования
 * @returns Экранированный текст
 */
export function escapeHtml(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char] || char);
}
